import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, User, Briefcase } from "lucide-react";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";
import EmailVerificationNotice from "@/components/EmailVerificationNotice";
import { startGoogleSignIn } from "@/lib/authRedirect";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "cleaner" ? "cleaner" : "customer");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [pendingMessage, setPendingMessage] = useState("");

  // "Become a Cleaner" can be tapped while already on /register — keep the role in sync with the
  // URL so the cleaner journey is never silently downgraded to a customer signup.
  useEffect(() => {
    if (params.get("role") === "cleaner") setRole("cleaner");
  }, [params]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await register({ ...form, role });
      if (data.verification_required) {
        setPendingEmail(data.email);
        setPendingMessage(data.message);
        return;
      }
      toast.success(role === "cleaner"
        ? "Let's set up your cleaner profile — a few quick steps to go."
        : "Account created — welcome to My Clean Connect™!");
      navigate(role === "cleaner" ? "/cleaner/onboarding" : "/customer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const google = () => startGoogleSignIn({ role }); // eslint-disable-line no-unused-vars -- Google sign-in temporarily hidden; kept for easy restore

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-5 py-12 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8"><Wordmark iconClass="h-8 w-auto" textClass="text-lg" /></Link>
          <h1 className="font-display text-3xl font-semibold">Create your free account</h1>
          <p className="text-muted-foreground mt-1">No registration fees. Ever.</p>

          {pendingEmail ? (
            <div className="mt-6 space-y-4">
              <EmailVerificationNotice email={pendingEmail} message={pendingMessage} />
              <p className="text-center text-sm text-muted-foreground">
                Already confirmed? <Link to="/login" className="text-accent font-medium">Log in</Link>
              </p>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-2 gap-3 mt-6">
            <button onClick={() => setRole("customer")} className={`p-4 rounded-xl border-2 text-left transition-colors ${role === "customer" ? "border-accent bg-accent/5" : "border-border"}`} data-testid="role-customer">
              <User className="h-5 w-5 text-accent mb-2" /><p className="font-medium">I need a cleaner</p>
            </button>
            <button onClick={() => setRole("cleaner")} className={`p-4 rounded-xl border-2 text-left transition-colors ${role === "cleaner" ? "border-accent bg-accent/5" : "border-border"}`} data-testid="role-cleaner">
              <Briefcase className="h-5 w-5 text-accent mb-2" /><p className="font-medium">I am a cleaner</p>
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4 mt-5">
            <input required value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Full name" className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="register-name" />
            <input type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="Email" className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="register-email" />
            <input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="Phone (optional)" className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="register-phone" />
            <input type="password" required value={form.password} onChange={(e) => upd("password", e.target.value)} placeholder="Password (min 6 chars)" className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="register-password" />
            <button type="submit" disabled={busy} className="w-full py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="register-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account? <Link to="/login" className="text-accent font-medium">Log in</Link>
          </p>
          </>
          )}
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.pexels.com/photos/4239146/pexels-photo-4239146.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative p-14 h-full flex items-end text-background">
          <h2 className="font-display text-4xl font-medium leading-tight">Join thousands earning and booking with confidence.</h2>
        </div>
      </div>
    </div>
  );
}
